import React, { useEffect, useState, Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faLightbulb } from '@fortawesome/free-solid-svg-icons';
import { Alert, Checkbox, Popconfirm, Select, Tooltip, notification } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { InfoCircleOutlined, PlusCircleFilled } from '@ant-design/icons';
import H3FormInput from '@Uilib/h3FormInput';
import H3Text from '@Uilib/h3Text';
import QCPointActions from '@Actions/quality/qcPointActions';
import CategoryActions from '@Actions/categoryActions';
import ProductActions from '@Actions/productActions';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import Helpers from '@Apis/helpers';
import QCCustomInputs from './QCCustomInputs';
import TenantSelector from '../../../Common/Selector/TenantSelector';
import './style.scss';
import RestrictedForm from '@Components/RestrictedForm';
import Crown from '@Images/crown2.png';
import QCRejectionNotification from './QCRejectionNotification';
import SelectAppUser from '@Components/Common/SelectAppUser';
import PRZSelect from '../../../Common/UI/PRZSelect';

const { Option } = Select;

/**
 *
 */
const QCPointForm = ({ createQCPointLoading, user, updateQCPointLoading, getCategories, selectedQCPoint, getProducts, products, callback, updateQCPoint, createQCPoint, productSkuId,
}) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [qcName, setQCName] = useState('');
  const [operations, setOperations] = useState([]);
  const [controlFrequency, setControlFrequency] = useState('');
  const [periodDay, setPeriodDay] = useState(null);
  const [productsData, setProductsData] = useState([]);
  const [productSkuIds, setProductSkuIds] = useState([]);
  const [customInputData, setCustomInputData] = useState([]);
  const [qcRejectionData, setQcRejectionData] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState([]);
  const [allProducts, setAllProducts] = useState(false);
  const [qcType, setQCType] = useState('');
  const [qcVerificationType, setQCVerificationType] = useState('');
  const [qcValidityPeriod, setQcValidityPeriod] = useState('');
  const [qcSamplePercentage, setQcSamplePercentage] = useState(0);
  const [enableQcRejectionNotification, setEnableQcRejectionNotification] = useState(true);
  const [defaultAssignees, setDefaultAssignees] = useState([]);
  const [sendNotification, setSendNotification] = useState(false);

  const enableQcSampling = user.tenant_info?.quality_control_config?.settings?.enable_sampling_in_qc && qcType !== 'QUANTITY';

  useEffect(() => {
    getCategories();
    getProducts(null, user?.tenant_info?.tenant_id, '', '', '', '', '', 'STORABLE,NON_STORABLE');
    if (selectedQCPoint) {
      setQCName(selectedQCPoint?.quality_control_name);
      setOperations(selectedQCPoint?.operations);
      setControlFrequency(selectedQCPoint?.control_frequency);
      setPeriodDay(selectedQCPoint?.period);
      setQCVerificationType(selectedQCPoint?.qc_verification_type);
      setQcSamplePercentage(selectedQCPoint?.sample_percentage || 0);
      setQcValidityPeriod(Number(selectedQCPoint?.qc_validity_in_days) === -1 ? '' : selectedQCPoint?.qc_validity_in_days);
      setQCType(selectedQCPoint?.control_per);
      setProductSkuIds(selectedQCPoint?.products?.map((item) => item?.product_sku_id));
      setCustomInputData(JSON.parse(selectedQCPoint?.additional_inputs)?.map((additionalInputs) => ({
        ...additionalInputs,
        key: uuidv4(),
      })) || []);
      setQcRejectionData(selectedQCPoint?.qc_rejection_notification_data ? JSON.parse(selectedQCPoint?.qc_rejection_notification_data) : null);
      setEnableQcRejectionNotification(selectedQCPoint?.enable_qc_rejection_notification);
      setSendNotification(selectedQCPoint?.send_notification_to_assignees);
      setDefaultAssignees(selectedQCPoint?.default_assignees);
    }
    if (productSkuId) {
      setProductSkuIds([productSkuId]);
    }
  }, []);

  useEffect(() => {
    if (products?.result?.products) {
      setProductsData(products?.result?.products);
      if (selectedQCPoint) {
        const filteredProductsData = products?.result?.products.filter((product) => selectedQCPoint?.products?.map((item) => item?.product_sku_id)?.includes(product?.product_sku_id));
        setProductsData(filteredProductsData);
      }
    }
  }, [products]);

  const operationsArray = [
    { id: 1, operationName: 'Internal Transfer Receipt', operationValue: 'good_receiving_note_internal_stock_transfer' },
    { id: 2, operationName: 'External Transfer Receipt', operationValue: 'good_receiving_note_external_stock_transfer' },
    { id: 3, operationName: 'Purchase Receipt', operationValue: 'good_receiving_note' },
    { id: 4, operationName: 'Manufacturing Order', operationValue: 'manufacturing_order' },
  ];
  const operationsOptions = operationsArray?.map((item) => ({
    label: item?.operationName,
    value: item?.operationValue,
  }));

  const isDataValid = () => {
    let isValid = true;
    if (customInputData?.length > 0) {
      for (let i = 0; i < customInputData.length; i++) {
        const item = customInputData[i];
        // Basic validation for all cases
        if (!item?.name || !item?.type || (item?.type === 'DROPDOWN' && item?.dropDown_values?.length === 0)) {
          isValid = false;
          break; // Exit the loop on the first invalid item
        }

        // Additional validation for Automatic QC verification
        if (qcVerificationType === 'AUTOMATIC' && !(item?.type === 'TEXT' || item?.type === 'TEXTAREA' || item?.type === 'ATTACHMENT')) {
          // Assuming pass_condition is an object with rule, value_1, and optionally value_2
          const passCondition = item.pass_condition;
          if (!passCondition?.rule || passCondition?.rule === '' || !passCondition?.value_1 || (passCondition?.rule === 'BETWEEN' && (!passCondition?.value_1 || !passCondition?.value_2))) {
            isValid = false;
            break; // Exit the loop if any condition for Automatic is not met
          }
        }
      }
    }

    // If isValid is false, show a notification to the user
    if (!isValid) {
      notification.error({
        message: 'Please fill all the fields in the additional quality check inputs section.',
        placement: 'top',
        duration: 4,
      });
    }

    return isValid;
  };

  const AddQCPoint = () => {
    setFormSubmitted(true);
    if (isDataValid() && qcName && operations?.length > 0 && qcType && qcVerificationType && productSkuIds?.length > 0 && controlFrequency && (['RANDOMLY', 'PERIODICALLY'].includes(controlFrequency) ? periodDay > 0 : true)) {
      if (selectedQCPoint) {
        const payload = {
          tenant_id: user?.tenant_info?.tenant_id,
          group_id: selectedQCPoint ? selectedQCPoint?.group_id : null,
          quality_control_name: qcName,
          operations,
          control_per: qcType,
          qc_verification_type: qcVerificationType,
          qc_validity_in_days: qcValidityPeriod ? Number(qcValidityPeriod) : -1,
          control_frequency: controlFrequency,
          period: Number(periodDay),
          enable_qc_rejection_notification: enableQcRejectionNotification,
          qc_rejection_notification_data: qcRejectionData,
          product_sku_ids: productSkuIds,
          sample_percentage: Number(qcSamplePercentage || 0),
          additional_inputs: customInputData.map((input) => ({
            ...input,
            pass_condition: input?.pass_condition?.rule === null && input?.pass_condition?.value_1 === null && input?.pass_condition?.value_2 === null ? null : input?.pass_condition,
          })),
          send_notification_to_assignees: sendNotification,
          default_assignees: defaultAssignees?.filter(assignee => assignee !== null) || [],
        };
        updateQCPoint(payload, () => callback());
      } else if (user?.org_id ? selectedTenant?.length > 0 : true) {
        const payload = {
          group_id: selectedQCPoint ? selectedQCPoint?.group_id : null,
          quality_control_name: qcName,
          operations,
          control_per: qcType,
          qc_verification_type: qcVerificationType,
          qc_validity_in_days: qcValidityPeriod ? Number(qcValidityPeriod) : -1,
          control_frequency: controlFrequency,
          period: Number(periodDay),
          product_sku_ids: allProducts ? [] : productSkuIds,
          additional_inputs: customInputData.map((input) => ({
            ...input,
            pass_condition: input?.pass_condition?.rule === null && input?.pass_condition?.value_1 === null && input?.pass_condition?.value_2 === null ? null : input?.pass_condition,
          })),
          tenant_ids: selectedTenant,
          all_products: allProducts,
          qc_rejection_notification_data: qcRejectionData,
          sample_percentage: Number(qcSamplePercentage || 0),
          enable_qc_rejection_notification: enableQcRejectionNotification,
          send_notification_to_assignees: sendNotification,
          default_assignees: defaultAssignees?.filter(assignee => assignee !== null) || [],
        };
        createQCPoint(payload, () => callback());
      }
    }
  };

  /**
   *
   */
  const addNewRow = () => {
    const copyData = JSON.parse(JSON.stringify(customInputData));
    copyData.push({
      key: uuidv4(),
      is_required: false,
      name: '',
      placeholder: '',
      type: '',
      dropDown_values: [],
      pass_condition: {
        rule: '',
        value_1: '',
        value_2: '',
      },
      showPlaceholder: false,
    });

    setCustomInputData(copyData);
  };

  const handleDelete = (key) => {
    // Check if it's in 'AUTOMATIC' mode and trying to delete the last remaining item
    if (qcVerificationType === 'AUTOMATIC' && customInputData.length <= 1) {
      notification.error({
        message: 'At least one additional quality check input is required for automatic QC verification.',
        placement: 'top',
        duration: 4,
      });
    } else {
      // Proceed with deletion as there are more than one items or it's not in AUTOMATIC mode
      const copyData = customInputData.filter((item) => item.key !== key);
      setCustomInputData(copyData);
    }
  };
  const qualityControlConfig = user?.tenant_info?.quality_control_config?.sub_modules?.quality_control_rule?.is_active;

  const samplingTypeOptions = [
    { label: 'Every', value: 'ALL' },
    { label: 'Randomly', value: 'RANDOMLY' },
    { label: 'Periodically', value: 'PERIODICALLY' },
  ];

  const attributeOptions = [
    { label: 'Product', value: 'PRODUCT' },
    { label: 'Quantity', value: 'QUANTITY' },
  ];

  const testModeOptions = [
    { label: 'Manual', value: 'PASS_FAIL' },
    { label: 'Automatic', value: 'AUTOMATIC' },
  ];

  return (
    <Fragment>
      <div className="org-qc-point-create__wrapper" style={{ marginBottom: '40px' }}>
        <div className="ant-row">

          <div className="ant-col-md-12 ant-col-xs-24">
            <H3FormInput
              name="title"
              type="text"
              containerClassName="orgInputContainer create__right-input"
              labelClassName="orgFormLabel"
              inputClassName="orgFormInput"
              placeholder=""
              onChange={(event) => setQCName(event.target.value)}
              value={qcName}
              disabled={createQCPointLoading || updateQCPointLoading}
              label="Title"
              maxlength="100"
              required
              showError={formSubmitted && !qcName}
            />
          </div>

          <div className="ant-col-md-12 ant-col-xs-24">
            <div className="orgInputContainer create__left-input">
              <div className="orgFormLabel">
                Operations
                {' '}
                <span style={{ color: 'red' }}>*</span>
              </div>
              <div>
                <PRZSelect
                  mode="multiple"
                  value={operations}
                  filterOption={false}
                  disabled={createQCPointLoading || updateQCPointLoading}
                  onChange={(value) => {
                    setOperations(value);
                  }}
                  showSearch
                  placeholder="Select operations"
                  maxTagCount="responsive"
                  required
                  options={operationsOptions}
                  showError={formSubmitted && !operations?.length}
                  errorName=" operations"
                  errorClassName="input-error"
                >
                </PRZSelect>
              </div>
            </div>
          </div>

          <div className="ant-col-md-12 ant-col-xs-24">
            {!selectedQCPoint ? (
              <TenantSelector
                selectedTenant={selectedTenant}
                noDropdownAlign
                showSearch
                onChange={(value) => {
                  setSelectedTenant(value);
                }}
                placeholder="Select Business Unit"
                labelClassName="orgFormLabel qc-form-label"
                inputClassName="create__right-input"
                isMultiple
                title="Business Units"
                includedTenants={Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.QUALITY_RULES, Helpers.permissionTypes.READ)}
                showError={formSubmitted && user?.org_id && selectedTenant?.length < 1}
                disabled={createQCPointLoading || updateQCPointLoading}
              />
            ) : (
              <div className="orgInputContainer create__left-input" />
            )}
          </div>

          <div className="ant-col-md-12 ant-col-xs-24">
            {!selectedQCPoint && !productSkuId && (
              <div className="orgInputContainer create__left-input">
                <div className="orgFormLabel">
                  Products
                  {' '}
                  <span style={{ color: 'red' }}>*</span>
                </div>
                <div>
                  <PRZSelect
                    mode="multiple"
                    value={productSkuIds}
                    filterOption={false}
                    disabled={createQCPointLoading || updateQCPointLoading || selectedQCPoint}
                    onChange={(value) => {
                      if (!value.includes('All')) {
                        const productsArr = [];
                        for (let i = 0; i < value?.length; i++) {
                          productsArr.push(value[i]);
                        }
                        setProductSkuIds(productsArr);
                        getProducts(null, user?.tenant_info?.tenant_id, '', '', '', '', '', 'STORABLE,NON_STORABLE');
                        setAllProducts(false);
                      } else {
                        setAllProducts(true);
                        setProductSkuIds(['All']);
                      }
                    }}
                    showSearch
                    onSearch={(input) => {
                      getProducts(input, user?.tenant_info?.tenant_id, '', '', '', '', '', 'STORABLE,NON_STORABLE');
                    }}
                    placeholder="Select a products"
                    maxTagCount="responsive"
                    showError={formSubmitted && productSkuIds?.length <= 0}
                    errorName=" products"
                    errorClassName="input-error"
                  >
                    {/* <Option value="All">All Products</Option> */}
                    {!productSkuIds.includes('All') && productsData
                      ?.filter((item) => item?.is_active)
                      ?.map((item) => (
                        <Option key={item?.product_sku_id} value={item?.product_sku_id}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ marginRight: '5px', whiteSpace: 'pre-wrap' }}>
                              {`#${item?.product_info?.internal_sku_code} ${item?.product_info?.ref_product_code ? item?.product_info?.ref_product_code : ''} - ${item?.product_info?.product_sku_name}`}
                            </div>
                            {item?.qc_id && (
                              <div style={{ color: 'green' }}>
                                <FontAwesomeIcon icon={faCircleCheck} />
                              </div>
                            )}
                          </div>
                        </Option>
                      ))}
                  </PRZSelect>
                </div>
              </div>
            )}
          </div>
          <div className="ant-col-md-12 ant-col-xs-24">
            <div className="orgInputContainer create__right-input">
              <label className="orgFormLabel">
                Control Per
                <span style={{ color: 'red' }}>{'  *'}</span>
                <Tooltip
                  title={(
                    <span
                      className="custom-tooltip-bg"
                    >
                      <b>QC Types Explained:</b>
                      <br />
                      -
                      {' '}
                      <b>Product Wise QC:</b>
                      {' '}
                      Batch-level QC for uniform batch production.
                      <br />
                      -
                      {' '}
                      <b>Quantity Wise QC:</b>
                      {' '}
                      Individual QC for high-value or varied items.
                    </span>
                  )}
                  color="rgb(209, 225, 249)"

                >
                  <InfoCircleOutlined style={{ marginLeft: '5px', color: 'rgb(45, 125, 247)', cursor: 'pointer' }} />
                </Tooltip>
              </label>
              <PRZSelect
                value={qcType}
                disabled={createQCPointLoading || updateQCPointLoading}
                onChange={(value) => {
                  if (value === 'QUANTITY') {
                    setQcSamplePercentage(0);
                  }
                  setQCType(value);
                }}
                options={attributeOptions}
                showError={formSubmitted && !qcType}
                errorName=" control per"
                errorClassName="input-error"
              />
            </div>
          </div>
          <div className="ant-col-md-12 ant-col-xs-24">
            <div className="orgInputContainer create__left-input">
              <label className="orgFormLabel">
                QC Verification Type
                <span style={{ color: 'red' }}>{'  *'}</span>
                <Tooltip
                  style={{
                    width: '400px',
                  }}
                  title={(
                    <span
                      className="custom-tooltip-bg"
                    >
                      <b>QC Verification Type:</b>
                      <br />
                      -
                      {' '}
                      <b>Pass Fail:</b>
                      {' '}
                      Simplified QC evaluation where batches are manually assessed to meet (pass) or not meet (fail) predefined criteria, facilitating straightforward quality control decisions based on specific inputs.
                      <br />
                      -
                      {' '}
                      <b>Automatic:</b>
                      {' '}
                      Advanced QC process where individual items or batches are automatically classified as pass or fail according to established rules, ensuring efficient and objective quality assessment without manual intervention.
                    </span>
                  )}
                  color="rgb(209, 225, 249)"

                >
                  <InfoCircleOutlined style={{ marginLeft: '5px', color: 'rgb(45, 125, 247)', cursor: 'pointer' }} />
                </Tooltip>
              </label>
              <PRZSelect
                value={qcVerificationType}
                disabled={createQCPointLoading || updateQCPointLoading}
                onChange={(value) => {
                  setQCVerificationType(value);
                  if (value === 'AUTOMATIC' && customInputData?.length === 0) {
                    addNewRow();
                  }
                  if (value === 'PASS_FAIL') {
                    setCustomInputData([]);
                  }
                }}
                options={testModeOptions}
                showError={formSubmitted && !qcVerificationType}
                errorName=" QC Verification Type"
                errorClassName="input-error"
              />
            </div>
          </div>
          <div className="ant-col-md-12 ant-col-xs-24">
            <div className="orgInputContainer create__right-input">
              <div className="create__with_selector">
                <div className="create__left-h3-input">
                  <H3FormInput
                    name="Validity days"
                    type="text"
                    containerClassName="orgInputContainer create__right-input"
                    labelClassName="orgFormLabel"
                    inputClassName="orgFormInput"
                    placeholder=""
                    onChange={(event) => setQcValidityPeriod(event.target.value)}
                    value={qcValidityPeriod}
                    disabled={createQCPointLoading || updateQCPointLoading}
                    label="QC Validity Period"
                    maxlength="100"
                  />
                </div>
                <PRZSelect
                  filterOption={false}
                  defaultValue="DAYS"
                  disabled={createQCPointLoading || updateQCPointLoading}
                  suffixIcon={null}
                >
                  <Option key="DAYS" value="DAYS">Days</Option>
                </PRZSelect>
              </div>
            </div>
          </div>

          <div className="ant-col-md-12 ant-col-xs-24">
            {enableQcSampling && (
              <div className="orgInputContainer create__left-input">
                <div className="create__with_selector">
                  <div className="create__right-h3-input">
                    <H3FormInput
                      name="Sample Percentage"
                      type="number"
                      className="orgInputContainer create__right-input"
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput"
                      onChange={(event) => setQcSamplePercentage(event.target.value)}
                      value={qcSamplePercentage}
                      disabled={qcType === 'QUANTITY' || createQCPointLoading || updateQCPointLoading}
                      label="QC Sample Quantity(%)"
                      maxlength="100"
                    />
                  </div>
                  <PRZSelect
                    filterOption={false}
                    defaultValue="PERCENTAGE(%)"
                    // disabled
                    suffixIcon={null}
                  >
                    <Option key="PERCENTAGE(%)" value="PERCENTAGE(%)">Percentage (%)</Option>
                  </PRZSelect>
                </div>
              </div>
            )}
          </div>

          <div className="ant-col-md-12 ant-col-xs-24">
            <div className="orgInputContainer create__right-input">
              <label className="orgFormLabel">
                Control Frequency
                <span style={{ color: 'red' }}>{'  *'}</span>
              </label>
              <PRZSelect
                value={controlFrequency}
                disabled={createQCPointLoading || updateQCPointLoading}
                onChange={(value) => {
                  setControlFrequency(value);
                }}
                options={samplingTypeOptions}
                showError={formSubmitted && !controlFrequency}
                errorName=" frequency"
                errorClassName="input-error"
              >
              </PRZSelect>
            </div>
          </div>

          {(controlFrequency === 'PERIODICALLY'
            ? (
              <div className="ant-col-md-12 ant-col-xs-24">
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px' }}>
                  <div className="qc-label-left">Every</div>
                  <H3FormInput
                    type="number"
                    label=" "
                    containerClassName="orgInputContainer create__right-input"
                    labelClassName="orgFormLabel"
                    inputClassName={(formSubmitted && periodDay <= 0) ? 'error-border' : 'orgFormInput'}
                    placeholder=""
                    disabled={createQCPointLoading || updateQCPointLoading}
                    onChange={(event) => setPeriodDay(event.target.value)}
                    value={periodDay}
                    maxlength="5"
                  />
                  <div className="qc-label-right">Days</div>
                </div>
              </div>
            )
            : (controlFrequency === 'RANDOMLY' ? (
              <div className="ant-col-md-12 ant-col-xs-24">
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px' }}>
                  <div className="qc-label-left">Every</div>
                  <H3FormInput
                    type="number"
                    label=" "
                    containerClassName="orgInputContainer create__right-input"
                    labelClassName="orgFormLabel"
                    inputClassName={(formSubmitted && periodDay <= 0) ? 'error-border' : 'orgFormInput'}
                    placeholder=""
                    disabled={createQCPointLoading || updateQCPointLoading}
                    onChange={(event) => {
                      setPeriodDay(event.target.value > 100 ? 100 : event.target.value);
                    }}
                    value={periodDay}
                    maxlength="100"
                    noDecimal
                  />
                  <div className="qc-label-right">% of Operations</div>
                </div>
              </div>
            )
              : <div className="ant-col-md-12 ant-col-xs-24" />
            ))}

          <div className="ant-col-md-24">
            <div className="form__input-row" style={{ marginBottom: '10px' }}>
              <Checkbox
                disabled={createQCPointLoading || updateQCPointLoading}
                checked={sendNotification}
                onChange={() => {
                  setSendNotification(!sendNotification);
                }}
              />
              <span
                style={{
                  fontWeight: '500',
                  fontSize: '12px',
                  marginLeft: '5px',
                }}
              >
                Notify assignees when a new quality check is assigned to them
              </span>
            </div>
          </div>

          <div className="ant-col-md-12 ant-col-xs-24">
            <div className="orgInputContainer create__right-input">
              <H3Text
                text="Default Assignees"
                className="orgFormLabel"
              />
              <SelectAppUser
                hideTitle
                labelClassName="orgFormLabel"
                inputClassName="orgFormInput"
                selectedUser={defaultAssignees || []}
                onChange={(value) => setDefaultAssignees(value)}
                disabled={createQCPointLoading || updateQCPointLoading}
                isMultiple
                placeholder="Select default assignee.."
                allUsers
                maxTagCount="responsive"
              />
            </div>
          </div>

          <div className="ant-col-md-24 ant-col-xs-24">
            <div className="qc-disclaimer-wrapper">
              {(!selectedQCPoint || qcVerificationType === 'AUTOMATIC') && (
                <React.Fragment>
                  <Alert
                    message={(
                      <div style={{ marginTop: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <FontAwesomeIcon icon={faLightbulb} color="#FFA500" />
                          &nbsp;
                          Additional Notes
                        </div>
                        {!selectedQCPoint && (
                          <ul>
                            <li>Existing quality control rule for a product you have selected will be overwritten.</li>
                          </ul>
                        )}
                        {qcVerificationType === 'AUTOMATIC' && (
                          <ul>
                            <li>In case of automatic QC verification, at least one additional quality check inputs is required.</li>
                          </ul>
                        )}
                        {qcType === 'QUANTITY' && (
                          <ul>
                            <li>Quantity-wise, a maximum of 50 Quality Checks can be initiated at one time.</li>
                          </ul>
                        )}
                      </div>
                    )}
                    type="warning"
                  />
                </React.Fragment>
              )}
            </div>
          </div>

          <div className="ant-col-md-24 ant-col-xs-24">
            <div className="qc__section">
              <H3Text text="Additional Quality Check Inputs" className="qc__section-title" />
              {customInputData?.length > 0 && (
                <QCCustomInputs
                  customInputData={customInputData}
                  updateCustomInputData={(updatedCustomInputData) => setCustomInputData(updatedCustomInputData)}
                  handleDelete={handleDelete}
                  qcVerificationType={qcVerificationType}
                />
              )}
              <div className="ant-row">
                <div className="ant-col-md-12">

                  <div className="new-row-button" onClick={() => addNewRow()}>
                    <span className="new-row-button__icon"><PlusCircleFilled /></span>
                    <div>New Input</div>
                  </div>
                </div>
                <div className="ant-col-md-4" />
              </div>
            </div>
          </div>

          <div className="ant-col-md-24 ant-col-xs-24">
            <div className="qc__section">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
                className="qc__rejection-title"
              >
                <div>
                  <Checkbox
                    checked={enableQcRejectionNotification}
                    onChange={() => setEnableQcRejectionNotification(!enableQcRejectionNotification)}
                  />
                </div>

                <div style={{ marginLeft: '5px' }}>
                  <H3Text text="Send automatic notification when quality check is rejected" />
                </div>
              </div>
              {enableQcRejectionNotification && (
                <QCRejectionNotification
                  qcRejectionData={qcRejectionData}
                  updateQcRejectionData={(updateQcRejectionData) => setQcRejectionData(updateQcRejectionData)}
                  handleDelete={handleDelete}
                  qcVerificationType={qcVerificationType}
                />
              )}
            </div>
          </div>

          <div className="custom-drawer__footer" style={{ width: '980px' }}>
            {!user?.tenant_info?.quality_control_config?.sub_modules?.quality_control_rule?.is_active && (<RestrictedForm moduleName="Quality Control" />)}
            <div className="ant-col-md-6" style={{ marginTop: '0.5rem' }}>
              <div className="indent-barcode__wrapper" style={{ display: 'flex' }}>
                <H3Button
                  buttonType={defaultButtonTypes.BLUE_ROUNDED}
                  text={selectedQCPoint ? 'Update Quality Rule' : 'Create Quality Rule'}
                  onClick={() => AddQCPoint()}
                  isLoading={createQCPointLoading || updateQCPointLoading}
                  disabled={createQCPointLoading || updateQCPointLoading || !qualityControlConfig}
                  style={{
                    width: '160px',
                    borderRadius: '5px',
                    padding: '7px 0px',
                    marginLeft: '0',
                  }}
                />
                {!qualityControlConfig && (
                  <Popconfirm
                    placement="topRight"
                    title="This feature is not accessible within your current plan to use this feature contact us."
                    onConfirm={() => window.Intercom('showNewMessage')}
                    okText="Contact Us"
                    cancelText="Cancel"
                  >
                    <img
                      className="barcode-restrict"
                      src={Crown}
                      alt="premium"
                      style={{
                        marginLeft: '8px',
                        marginTop: '5px',
                      }}
                    />
                  </Popconfirm>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, QCPointReducers, CategoryReducers, ProductReducers,
}) => ({
  user: UserReducers.user,
  createQCPointLoading: QCPointReducers.createQCPointLoading,
  updateQCPointLoading: QCPointReducers.updateQCPointLoading,
  categories: CategoryReducers.categories,
  products: ProductReducers.products,
});

const mapDispatchToProps = (dispatch) => ({
  createQCPoint: (payload, callback) => dispatch(QCPointActions.createQCPoint(payload, callback)),
  updateQCPoint: (payload, callback) => dispatch(QCPointActions.updateQCPoint(payload, callback)),
  getCategories: (orgId, page, limit, searchKeyword) => dispatch(CategoryActions.getCategories(orgId, page, limit, searchKeyword)),
  getProducts: (keyword, tenantId, stockStatus, limit, page, tenantDepartmentId, excludeOutOfStock, productType, category, isSalesProduct, isPurchaseProduct) => dispatch(ProductActions.getProducts(keyword, tenantId, stockStatus, limit, page, tenantDepartmentId, excludeOutOfStock, productType, category, isSalesProduct, isPurchaseProduct)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(QCPointForm));
